import { FC, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSearchSuggestionList } from 'api/misc';
import useDebounce from 'hooks/useDebounce';
import React from 'react';
import { useSearchBarContext } from './context';
import { GET_SEARCH_SUGGESTION_LIST } from './queries';
import SearchBar from './SearchBar';
import { SearchSuggestion } from './types';
import { InspirationType } from 'types/types';

interface IInspirationItem {
  inspirations: InspirationType[];
  initialSearchValue?: string;
}

const SearchBarContainer: FC<IInspirationItem> = ({ inspirations, initialSearchValue }) => {
  const { state, setState } = useSearchBarContext();
  const { searchInputValue } = state;

  useEffect(() => {    
    if (initialSearchValue !== undefined) {
      setState({ searchInputValue: initialSearchValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchValue]);

  // Debounce the search value to avoid unnecessary API calls
  const debouncedSearchInputValue = useDebounce(searchInputValue, 200);

  // Fetch the search suggestion list
  const {
    data: searchSuggestionList,
    isLoading: isLoadingSearchSuggestionList,
  } = useQuery<SearchSuggestion[]>(
    [GET_SEARCH_SUGGESTION_LIST, debouncedSearchInputValue],
    () => getSearchSuggestionList(debouncedSearchInputValue),
    {
      keepPreviousData: true, // UX: keep the previous autocomplete data while loading new data
      staleTime: 1000 * 60, // 1 minute
    }
  );

  const noResultsFound = React.useMemo(
    () =>
      searchSuggestionList?.length === 0 &&
      !isLoadingSearchSuggestionList &&
      debouncedSearchInputValue.length >= 3,
    [searchSuggestionList, isLoadingSearchSuggestionList, debouncedSearchInputValue]
  );

  // Sync the data state with the current context state
  React.useEffect(() => {
    setState({
      searchSuggestionList,
      isLoadingSearchSuggestionList,
      debouncedSearchInputValue,
      noResultsFound,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSuggestionList]);

  return <SearchBar inspirationCategories={inspirations} />;
};

export default SearchBarContainer;
